import * as yup from 'yup';
import { updateCycleMetaData } from 'actions/envScales/observationCycles';
import { MetaDataCategory } from 'types/api/envScales/MetaDataCategory';
import { SectionWithBorder } from 'components/Measure/View/Styled';
import { useMediaQuery } from 'react-responsive';
import { Box } from 'components/Box';
import { useEffect, useState } from 'react';
import Typography from 'components/Typography';
import { ObservationCycle } from 'types/api/envScales/ObservationCycle';
import { MetaDataAnswer } from 'types/api/envScales/MetaDataAnswer';
import Question from './Question';
import { Flex } from 'components/Flex';
import { addValue, FormatChoice, getInitialState, removeValue } from './utils';
import { useValidations } from 'components/Measure/View/ValidationsContext';
import { ValidationError } from 'types/api/envScales/ObservationValidations';
import clsx from 'clsx';

function displayTitle(title: string) {
  if (!title) {
    return '';
  }

  const [text, textInParentheses] = title.split('(');
  return (
    <>
      {text}{' '}
      {textInParentheses && <Typography>({textInParentheses}</Typography>}
    </>
  );
}

interface MetaDataCategoryFormProps {
  categories: MetaDataCategory[];
  cycle: ObservationCycle;
  observationId: number;
}

function MetaDataCategoryForm({
  categories,
  cycle,
  observationId
}: MetaDataCategoryFormProps) {
  const [metaDataAnswers, setMetaDataAnswers] = useState<MetaDataAnswer[]>(
    cycle.meta_data_answers
  );
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const contentCategory = categories.find(
    category => category.t_name === 'content'
  );
  const formatCategory = categories.find(
    category => category.t_name === 'format'
  );
  const choices = contentCategory?.choices;
  const formatChoices = formatCategory?.choices;
  const [metaDataValues, setMetaDataValues] = useState<FormatChoice>(
    getInitialState(choices, cycle.cycle_meta_data_values)
  );

  const { errors, showErrorStatus, reportFieldResolution } = useValidations();

  // Plain function, not a hook: called per-choice/question in a loop below,
  // which hooks can't do. Collects results for one effect call instead.
  const resolutions: Array<[string, boolean]> = [];

  function fieldError(
    path: string,
    currentValue: unknown,
    schema: yup.Schema
  ): ValidationError | null {
    const isValidNow = schema.isValidSync(currentValue);
    resolutions.push([path, isValidNow]);

    if (!showErrorStatus) {
      return null;
    }

    const error = errors[path];
    return !error || isValidNow ? null : error;
  }

  function handleRadioInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (
      event.currentTarget.dataset.questionId === undefined ||
      event.currentTarget.dataset.choiceId === undefined ||
      event.currentTarget.dataset.isFirstQuestion === undefined
    ) {
      return;
    }

    const questionId = parseInt(event.currentTarget.dataset.questionId);
    const value = parseInt(event.currentTarget.value);

    const payload = {
      meta_data: {
        meta_data_category_id: contentCategory?.id,
        question_id: questionId,
        answer: value
      }
    };
    const tempMetaDataAnswer: MetaDataAnswer = {
      meta_data_question_id: questionId,
      observation_cycle_id: cycle.id,
      value
    };
    let answers = metaDataAnswers.filter(
      ans =>
        ans.meta_data_question_id !== questionId ||
        cycle.id !== ans.observation_cycle_id
    );
    setMetaDataAnswers([...answers, tempMetaDataAnswer]);

    updateCycleMetaData(observationId, cycle.id, payload).then(({ data }) => {
      setMetaDataAnswers(data.meta_data_answers);
      setMetaDataValues(getInitialState(choices, data.cycle_meta_data_values));
    });
  }

  function handleCheckboxInputChange(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    if (
      event.currentTarget.dataset.categoryId === undefined ||
      event.currentTarget.dataset.choiceId === undefined
    ) {
      return;
    }

    const categoryId = parseInt(event.currentTarget.dataset.categoryId);
    const choiceId = parseInt(event.currentTarget.dataset.choiceId);
    const value = event.currentTarget.value;

    let newValues: FormatChoice = {};
    if (event.currentTarget.checked) {
      newValues = addValue(value, choiceId, metaDataValues);
    } else {
      newValues = removeValue(value, choiceId, metaDataValues);
    }
    setMetaDataValues(newValues);

    const payload = {
      meta_data: {
        meta_data_category_id: categoryId,
        meta_data_choice_id: choiceId,
        values: newValues[choiceId].map(item => {
          return { value: item.value, is_primary: false };
        })
      }
    };

    updateCycleMetaData(observationId, cycle.id, payload).then(({ data }) => {
      setMetaDataValues(getInitialState(choices, data.cycle_meta_data_values));
    });
  }

  const choiceValidations = (choices || []).map(choice => {
    const firstQuestion = choice.meta_data_questions[0];
    const firstAnswer = metaDataAnswers.find(
      answer =>
        answer.meta_data_question_id === firstQuestion?.id &&
        cycle.id === answer.observation_cycle_id
    );

    const isExpanded = firstAnswer && firstAnswer.value > 1;

    const questionErrors = choice.meta_data_questions.map((question, index) => {
      const answer = metaDataAnswers.find(
        ans =>
          ans.meta_data_question_id === question.id &&
          cycle.id === ans.observation_cycle_id
      );

      const isRequired = index === 0 || isExpanded;

      return fieldError(
        `cycles.${cycle.cycle_number}.details.content_and_format.${choice.t_name}.questions.${question.id}`,
        answer?.value,
        isRequired ? yup.number().required() : yup.number()
      );
    });

    const someQuestionNotAnswered = questionErrors.some(error => !!error);

    const formatsFieldError = fieldError(
      `cycles.${cycle.cycle_number}.details.content_and_format.${choice.t_name}.formats`,
      metaDataValues[choice.id] ?? [],
      isExpanded ? yup.array().min(1) : yup.array()
    );
    const hasFormatError = !!formatsFieldError;

    return {
      choice,
      firstQuestion,
      firstAnswer,
      isExpanded,
      questionErrors,
      someQuestionNotAnswered,
      formatsFieldError,
      hasFormatError
    };
  });

  useEffect(() => {
    resolutions.forEach(([path, isResolved]) =>
      reportFieldResolution(path, isResolved)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(resolutions)]);

  if (!contentCategory) {
    return null;
  }

  return (
    <>
      <div>
        <div className="font-bold">{contentCategory!.name}</div>
      </div>

      <Box mt="12px" mb="27px">
        {contentCategory!.description}
      </Box>

      {choiceValidations.map(
        ({
          choice,
          firstQuestion,
          firstAnswer,
          isExpanded,
          questionErrors,
          someQuestionNotAnswered,
          formatsFieldError,
          hasFormatError
        }) => {
          return (
            <SectionWithBorder
              key={choice.id}
              error={someQuestionNotAnswered || hasFormatError}
              className="mt-3"
            >
              <Box className="w-full max-w-5xl mx-auto">
                {(() => {
                  const titleNode = (
                    <div className="font-bold uppercase">{choice.name}</div>
                  );
                  const foldTitleIntoFirstQuestion =
                    isDesktop && !!firstQuestion;

                  return (
                    <>
                      {!foldTitleIntoFirstQuestion && titleNode}

                      {firstQuestion && (
                        <Question
                          question={firstQuestion}
                          showLabels
                          handleRadioInputChange={handleRadioInputChange}
                          answer={firstAnswer}
                          error={!!questionErrors[0]}
                          isFirstQuestion
                          titleSlot={
                            foldTitleIntoFirstQuestion ? titleNode : undefined
                          }
                        />
                      )}
                    </>
                  );
                })()}

                <div
                  className={clsx(
                    'overflow-hidden transition-[max-height] duration-700',
                    isExpanded ? 'max-h-[100rem]' : 'max-h-0'
                  )}
                >
                  <div className="pb-4">
                    <div>
                      {choice.meta_data_questions.map((question, index) => {
                        const answer = metaDataAnswers.find(
                          answer =>
                            answer.meta_data_question_id === question.id &&
                            cycle.id === answer.observation_cycle_id
                        );

                        return (
                          index > 0 && (
                            <Question
                              key={question.id}
                              question={question}
                              showLabels={!isDesktop}
                              handleRadioInputChange={handleRadioInputChange}
                              answer={answer}
                              error={!!questionErrors[index]}
                              isFirstQuestion={false}
                            />
                          )
                        );
                      })}
                    </div>

                    <div>
                      <hr className="border border-solid border-white" />

                      <Box mt="35px">
                        <div>
                          <div className="font-bold">
                            {displayTitle(formatCategory?.description || '')}
                          </div>
                          {hasFormatError && (
                            <p className="text-scale-red text-[14px] mt-0">
                              {formatsFieldError?.message}
                            </p>
                          )}
                        </div>
                      </Box>

                      <div className="grid gap-6 ml-[5px] mt-8 lg:grid-cols-3">
                        {formatChoices &&
                          metaDataValues[choice.id] &&
                          formatChoices.map(formatChoice => {
                            const checkBoxInputIdentifier = `${choice.id}_${formatChoice.id}`;
                            const isChecked = !!metaDataValues[choice.id].find(
                              item => item.value === formatChoice.t_name
                            );

                            return (
                              <Flex
                                align="center"
                                key={checkBoxInputIdentifier}
                              >
                                <input
                                  type="checkbox"
                                  className="appearance-none w-8 h-8 bg-white border-[0.5px] border-solid border-black rounded mr-[19px] cursor-pointer checked:bg-[#4A8263] checked:border-0 [&+label]:cursor-pointer focus:outline focus:outline-1 focus:outline-[#4A8263] focus:outline-offset-[3px]"
                                  id={checkBoxInputIdentifier}
                                  name={checkBoxInputIdentifier}
                                  onChange={handleCheckboxInputChange}
                                  data-choice-id={choice.id}
                                  data-choice-name={choice.t_name}
                                  data-category-id={formatCategory.id}
                                  value={formatChoice.t_name}
                                  checked={isChecked}
                                />
                                <label htmlFor={checkBoxInputIdentifier}>
                                  {formatChoice.name}
                                </label>
                              </Flex>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </div>
              </Box>
            </SectionWithBorder>
          );
        }
      )}
    </>
  );
}

export default MetaDataCategoryForm;
